import { Table, Button, Tag, Typography, Spin, Modal, Tooltip } from 'antd';
import {
	EditOutlined,
	DeleteOutlined,
	PlusOutlined,
	UnorderedListOutlined,
	CalendarOutlined,
	CheckSquareOutlined,
	FileTextOutlined,
	NumberOutlined,
	BarsOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { TSmartVariableDefinition } from '@/types/TSmartCRM';
import { useState } from 'react';

const { Text, Title } = Typography;
const { confirm } = Modal;

interface FieldManagementTableProps {
	fields: TSmartVariableDefinition[];
	loading?: boolean;
	onAddField: () => void;
	onEditField: (field: TSmartVariableDefinition) => void;
	onDeleteField: (fieldId: string) => void;
	deletingFieldId?: string | null;
}

export const FieldManagementTable = ({
	fields,
	loading = false,
	onAddField,
	onEditField,
	onDeleteField,
	deletingFieldId,
}: FieldManagementTableProps) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const getFieldTypeDisplay = (type: string): string => {
		return type;
	};

	const getFieldTypeIcon = (type: string): JSX.Element => {
		const iconMap: Record<string, JSX.Element> = {
			'Pick From List': <UnorderedListOutlined className="text-blue-500" />,
			Date: <CalendarOutlined className="text-green-500" />,
			'Yes/No': <CheckSquareOutlined className="text-purple-500" />,
			'Multiple Items': <BarsOutlined className="text-orange-500" />,
			Text: <FileTextOutlined className="text-gray-500" />,
			Number: <NumberOutlined className="text-red-500" />,
		};
		return iconMap[type] || <FileTextOutlined className="text-gray-500" />;
	};

	const handleDeleteClick = (record: TSmartVariableDefinition) => {
		confirm({
			title: 'Delete Field',
			icon: <ExclamationCircleOutlined />,
			content: (
				<div>
					<p>
						Are you sure you want to delete the field <strong>"{record.fieldName}"</strong>?
					</p>
					<p className="text-gray-500 mt-2">
						This action cannot be undone and will remove all data associated with this field.
					</p>
				</div>
			),
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				onDeleteField(record.id);
			},
		});
	};

	const handleTableChange = (pagination: any) => {
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const columns = [
		{
			title: 'Field Name',
			dataIndex: 'fieldName',
			key: 'fieldName',
			width: 180,
			render: (fieldName: string) => (
				<Text strong className="text-gray-900">
					{fieldName}
				</Text>
			),
		},
		{
			title: 'Internal Key',
			dataIndex: 'internalKey',
			key: 'internalKey',
			width: 130,
			render: (key: string) => <Text className="text-gray-600 font-mono text-sm">{key}</Text>,
		},
		{
			title: 'Type',
			dataIndex: 'fieldType',
			key: 'fieldType',
			width: 140,
			render: (fieldType: string) => (
				<div className="flex items-center gap-2">
					{getFieldTypeIcon(fieldType)}
					<Text className="text-gray-700">{getFieldTypeDisplay(fieldType)}</Text>
				</div>
			),
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
			width: 200,
			render: (description: string) => (
				<Text className="text-gray-500 text-sm">{description || '-'}</Text>
			),
		},
		{
			title: (
				<div className="flex items-center gap-1 text-sm">
					<span>Required for Leads Import</span>
					<Tooltip
						title="When adding new customers, this field must be filled out. It becomes a required field in the add new customer form and ensures important information is never missed."
						placement="top"
						overlayStyle={{ maxWidth: 300 }}
					>
						<InfoCircleOutlined className="text-gray-400 cursor-help" />
					</Tooltip>
				</div>
			),
			dataIndex: 'isRequired',
			key: 'isRequired',
			width: 180,
			render: (isRequired: boolean) => (
				<Tag color={isRequired ? 'red' : 'default'}>{isRequired ? 'Required' : 'Optional'}</Tag>
			),
		},
		{
			title: 'Options',
			dataIndex: 'options',
			key: 'options',
			width: 200,
			render: (options: string[]) => {
				if (!options || options.length === 0) {
					return <Text className="text-gray-400">-</Text>;
				}

				const visibleOptions = options.slice(0, 3);
				const remainingCount = options.length - visibleOptions.length;

				return (
					<div className="flex flex-wrap gap-1">
						{visibleOptions.map((option, index) => (
							<Tag key={index} color="blue" className="text-xs">
								{option}
							</Tag>
						))}
						{remainingCount > 0 && (
							<Tag color="default" className="text-xs">
								+{remainingCount} more
							</Tag>
						)}
					</div>
				);
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			render: (_: unknown, record: TSmartVariableDefinition) => (
				<div className="flex space-x-2">
					<Button
						type="text"
						icon={<EditOutlined />}
						size="small"
						className="text-blue-600 hover:text-blue-800"
						onClick={() => onEditField(record)}
						disabled={!!deletingFieldId}
					/>
					<Button
						type="text"
						icon={deletingFieldId === record.id ? <Spin size="small" /> : <DeleteOutlined />}
						size="small"
						className="text-red-600 hover:text-red-800"
						onClick={() => handleDeleteClick(record)}
						disabled={!!deletingFieldId}
						loading={deletingFieldId === record.id}
					/>
				</div>
			),
		},
	];

	return (
		<div className="bg-white -mt-6">
			<div className="p-4 sm:p-6 border-b border-gray-200">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
					<div className="flex-1">
						<Title level={3} className="m-0 text-gray-900">
							Customer Information Fields
						</Title>
						<Text className="text-gray-500">
							Track what's important for your business – create custom fields to remember details
							about each customer
						</Text>
					</div>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={onAddField}
						className="bg-blue-600 border-blue-600 hover:bg-blue-700 shrink-0"
						disabled={loading}
					>
						<span className="hidden sm:inline">Add New Field</span>
						<span className="sm:hidden">Add Field</span>
					</Button>
				</div>
			</div>

			<div className="p-4 sm:p-6 overflow-auto">
				<Table
					dataSource={fields}
					columns={columns}
					rowKey="id"
					loading={loading}
					scroll={{ x: 1000, y: 'calc(100vh - 450px)' }}
					onChange={handleTableChange}
					pagination={{
						current: currentPage,
						pageSize,
						showSizeChanger: true,
						showQuickJumper: false,
						pageSizeOptions: ['10', '20', '50', '100'],
						showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} fields`,
					}}
					className="w-full"
					bordered={false}
					size="middle"
				/>
			</div>
		</div>
	);
};
